import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Bold, Italic, UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered,
  Heading1, Heading2, Heading3,
  Undo2, Redo2, Save, Download, X, Loader2, FileText,
} from 'lucide-react';
import api from '../lib/api';

/**
 * DocumentEditor — full-fidelity WYSIWYG editor using an iframe with designMode='on'.
 * This preserves ALL CSS, tables, layout from the original HTML while still allowing
 * inline text editing. Toolbar operates via document.execCommand on the iframe doc.
 */
export default function DocumentEditor({ doc, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const iframeRef = useRef(null);
  const initialHtmlRef = useRef('');

  // Fetch HTML payload
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/employee-documents/${doc.id}/html`)
      .then(res => {
        if (cancelled) return;
        const html = res.data?.html_content || '';
        initialHtmlRef.current = html;
        setMeta(res.data);
        setHtmlContent(html);
        setLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        toast.error(e.response?.data?.detail || 'Failed to load document');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [doc]);

  // Inject HTML into iframe ONLY when both iframe is mounted AND html is ready
  useEffect(() => {
    if (loading || !htmlContent) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    const idoc = iframe.contentDocument || iframe.contentWindow.document;
    if (!idoc) return;
    idoc.open();
    idoc.write(htmlContent);
    idoc.close();
    // Enable editing once the iframe doc is ready
    const enable = () => {
      try { idoc.designMode = 'on'; } catch (e) {}
      idoc.addEventListener('input', () => setDirty(true));
      idoc.addEventListener('click', (e) => {
        if (e.target.closest('a')) e.preventDefault();
      });
    };
    if (idoc.readyState === 'complete') {
      enable();
    } else {
      idoc.addEventListener('DOMContentLoaded', enable, { once: true });
    }
  }, [loading, htmlContent]);

  // Execute a formatting command on the iframe's document
  const exec = useCallback((cmd, value = null) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const idoc = iframe.contentDocument;
    if (!idoc) return;
    iframe.contentWindow.focus();
    try {
      idoc.execCommand(cmd, false, value);
      setDirty(true);
    } catch (e) {
      console.warn('execCommand failed:', cmd, e);
    }
  }, []);

  const getCurrentHtml = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) return '';
    return '<!DOCTYPE html>' + iframe.contentDocument.documentElement.outerHTML;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const html = getCurrentHtml();
      const res = await api.put(`/employee-documents/${doc.id}/html`, { html_content: html });
      toast.success(`Saved as v${res.data?.version}`);
      initialHtmlRef.current = html;
      setDirty(false);
      onSaved && onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      let activeId = doc.id;
      if (dirty) {
        const html = getCurrentHtml();
        const res = await api.put(`/employee-documents/${doc.id}/html`, { html_content: html });
        activeId = res.data?.id || doc.id;
        initialHtmlRef.current = html;
        setDirty(false);
        toast.success(`Saved as v${res.data?.version}`);
        onSaved && onSaved(res.data);
      }
      const res = await api.get(`/employee-documents/${activeId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      const baseName = (doc.file_name || doc.doc_type || 'document').replace(/\.[^.]+$/, '');
      a.href = url; a.download = `${baseName}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = useCallback(() => {
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose && onClose();
  }, [dirty, onClose]);

  if (!doc) return null;

  return createPortal((
    <div className="fixed inset-0 z-[100] w-screen h-screen bg-slate-900/70 backdrop-blur-sm flex flex-col" data-testid="document-editor">
      {/* Top Bar */}
      <div className="bg-white border-b shadow-sm flex items-center justify-between px-4 py-2.5 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">{doc.title}</div>
            <div className="text-[11px] text-muted-foreground truncate">
              {doc.employee_name} · {doc.file_name}
              {meta && <> · <Badge variant="secondary" className="text-[10px] font-mono ml-1">v{doc.version}</Badge></>}
              {dirty && <span className="ml-2 text-amber-600">• Unsaved changes</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={handleSave} disabled={!dirty || saving} data-testid="editor-save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save as new version
          </Button>
          <Button size="sm" onClick={handleDownloadPdf} disabled={downloading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="editor-pdf-btn">
            {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            Download as PDF
          </Button>
          <Button variant="ghost" size="icon" onClick={handleClose} data-testid="editor-close-btn"><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Toolbar */}
      {!loading && (
        <div className="bg-white border-b px-3 py-1.5 flex items-center gap-0.5 flex-wrap shrink-0" data-testid="editor-toolbar">
          <ToolButton onClick={() => exec('undo')} icon={Undo2} label="Undo" />
          <ToolButton onClick={() => exec('redo')} icon={Redo2} label="Redo" />
          <Sep />
          <ToolButton onClick={() => exec('formatBlock', '<h1>')} icon={Heading1} label="H1" />
          <ToolButton onClick={() => exec('formatBlock', '<h2>')} icon={Heading2} label="H2" />
          <ToolButton onClick={() => exec('formatBlock', '<h3>')} icon={Heading3} label="H3" />
          <ToolButton onClick={() => exec('formatBlock', '<p>')} label="P" textBtn />
          <Sep />
          <ToolButton onClick={() => exec('bold')} icon={Bold} label="Bold (Ctrl+B)" />
          <ToolButton onClick={() => exec('italic')} icon={Italic} label="Italic (Ctrl+I)" />
          <ToolButton onClick={() => exec('underline')} icon={UnderlineIcon} label="Underline (Ctrl+U)" />
          <ToolButton onClick={() => exec('strikeThrough')} icon={Strikethrough} label="Strikethrough" />
          <Sep />
          <ToolButton onClick={() => exec('justifyLeft')} icon={AlignLeft} label="Align left" />
          <ToolButton onClick={() => exec('justifyCenter')} icon={AlignCenter} label="Align center" />
          <ToolButton onClick={() => exec('justifyRight')} icon={AlignRight} label="Align right" />
          <Sep />
          <ToolButton onClick={() => exec('insertUnorderedList')} icon={List} label="Bullet list" />
          <ToolButton onClick={() => exec('insertOrderedList')} icon={ListOrdered} label="Numbered list" />
          <Sep />
          <div className="flex items-center gap-1 px-1">
            <label className="text-[11px] text-muted-foreground">Color</label>
            <input
              type="color"
              onChange={e => exec('foreColor', e.target.value)}
              className="w-6 h-6 border-0 cursor-pointer"
              data-testid="editor-color"
            />
          </div>
        </div>
      )}

      {/* Editable iframe surface */}
      <div className="flex-1 min-h-0 relative bg-slate-200">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            <span className="ml-2 text-sm text-muted-foreground">Loading document…</span>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            title="document-editor-frame"
            className="absolute inset-0 w-full h-full border-0 bg-white block"
            data-testid="editor-iframe"
          />
        )}
      </div>
    </div>
  ), document.body);
}

function ToolButton({ onClick, icon: Icon, label, textBtn }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="p-1.5 rounded transition hover:bg-slate-100 text-slate-600 min-w-[28px]"
    >
      {textBtn ? <span className="text-xs font-semibold">{label}</span> : <Icon className="h-4 w-4" />}
    </button>
  );
}

function Sep() {
  return <div className="w-px h-5 bg-slate-200 mx-1" />;
}
